import { Fragment, useState } from 'react'
import { Card, CardBody, Modal } from 'react-bootstrap'
import SelectAutoCompleteFilter from '../../components/SelectAutocompleteFilter'
import { API_URL } from '../../constants'
import {
  useInterventiList,
  useInterventiListInfinite,
} from '../../hooks/interventi'
import { calcGiorni, getLabelTipoBilancia } from '../../utils/utils'
import dayjs from 'dayjs'
import StickyTable from '../../components/StickyTable'
import CalendarField from '../../components/CalendarField'
import { useIsMobile } from '../../hooks/useMediaQuery'
import PianificazioneFooter, {
  PianificazioneStep,
  PianificazioneTabs,
} from './PianificazioneControls'

interface ModalConfirmProps {
  isOpen: boolean
  toggle: () => void
  save: (values: any) => Promise<void>
  cliente_id?: number
  data_intervento?: string
}

export default function ModalPianificazionePeriodica({
  isOpen,
  toggle,
  save,
  cliente_id,
  data_intervento,
}: ModalConfirmProps) {
  const [cliente, setCliente] = useState(cliente_id ? String(cliente_id) : '')

  const {
    data: interventi,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInterventiListInfinite({
    cliente,
    stato: 'richiesto',
    tipo_intervento: 'verifica_periodica',
  })

  const [interventiSelected, setInterventiSelected] = useState<number[]>([])
  const [dataIntervento, setDataIntervento] = useState(data_intervento ?? '')
  const [note, setNote] = useState('')
  const [utente, setUtente] = useState<number | null>(null)
  const [step, setStep] = useState<PianificazioneStep>('selezione')
  const isMobile = useIsMobile()

  const operatoreField = (
    <div className={isMobile ? undefined : 'mt-4'}>
      <div className="form-group">
        <label className="label-m text-body-secondary">
          Operatori
        </label>
      </div>
      <SelectAutoCompleteFilter
        value={utente}
        placeholder="Seleziona un operatore"
        onChange={(value: any) =>
          setUtente(value ? value.value : null)
        }
        url={`${API_URL}/utenti/users/options-operatori`}
        isMulti={false}
      />
    </div>
  )

  return (
    <Modal
      show={isOpen}
      onHide={toggle}
      centered
      size="xl"
      fullscreen="md-down"
      backdropClassName="backdrop-high"
      className="modal-pianificazione"
    >
      <Modal.Header closeButton>
        <Modal.Title>Pianificazione intervento periodico</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-pianificazione-body" data-step={step}>
        <PianificazioneTabs
          step={step}
          setStep={setStep}
          labelSelezione="Interventi"
          selectedCount={interventiSelected.length}
        />
        <div className="row mt-2 pianificazione-row">
          <div className="col-md-8 pianificazione-selezione">
            <div className="d-flex justify-content-between align-items-end flex-wrap gap-2">
              {!cliente_id ? (
                <div className="form-group mb-2" style={{ width: '300px', maxWidth: '100%' }}>
                  <label className="label-m text-body-secondary">Cliente</label>

                  <SelectAutoCompleteFilter
                    value={cliente}
                    menuPortalTarget={false}
                    onChange={(value: any) =>
                      setCliente(value ? value.value : '')
                    }
                    url={`${API_URL}/bilance/cliente/options`}
                    isMulti={false}
                  />
                </div>
              ) : (
                <div />
              )}

              <div className="mb-2 text-body-secondary">
                {interventiSelected.length} su{' '}
                {interventi?.pages
                  ?.map((pagina) => pagina.results.length)
                  .reduce((a, b) => a + b, 0) ?? 0}{' '}
                interventi selezionati
              </div>
            </div>
            <Card>
              <CardBody>
                <StickyTable
                  isLoading={isLoading}
                  className="StickyInModal"
                  onEndReached={hasNextPage ? fetchNextPage : undefined}
                >
                  <thead className="position-sticky top-0 bg-white border-bottom border-top">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInterventiSelected(
                                interventi?.pages
                                  ?.map((pagina) =>
                                    pagina.results.map(
                                      (intervento) => intervento.id
                                    )
                                  )
                                  .flat() ?? []
                              )
                            } else {
                              setInterventiSelected([])
                            }
                          }}
                          checked={
                            interventiSelected.length ===
                            (
                              interventi?.pages
                                ?.map((pagina) =>
                                  pagina.results.map(
                                    (intervento) => intervento.id
                                  )
                                )
                                .flat() ?? []
                            ).length
                          }
                        />
                      </th>
                      <th>Bilancia</th>
                      <th>Cliente</th>
                      <th>Data richiesta</th>
                      <th>GG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interventi?.pages?.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Nessun intervento disponibile
                        </td>
                      </tr>
                    )}
                    {interventi?.pages?.map((pagina, i) => (
                      <Fragment key={i}>
                        {pagina.results.map((intervento, j) => (
                          <Fragment key={intervento.id}>
                            <tr>
                              <td>
                                <input
                                  type="checkbox"
                                  onChange={() => {
                                    if (
                                      interventiSelected.includes(intervento.id)
                                    ) {
                                      setInterventiSelected(
                                        interventiSelected.filter(
                                          (id) => id !== intervento.id
                                        )
                                      )
                                    } else {
                                      setInterventiSelected([
                                        ...interventiSelected,
                                        intervento.id,
                                      ])
                                    }
                                  }}
                                  checked={interventiSelected.includes(
                                    intervento.id
                                  )}
                                />
                              </td>
                              <td className="p-sm">
                                {intervento.bilancia_data?.matricola}
                                {intervento.bilancia_data?.sigla
                                  ? `(${intervento.bilancia_data?.sigla})`
                                  : ''}
                                <div className="text-body-secondary">
                                  {getLabelTipoBilancia(
                                    intervento.bilancia_data?.tipo_bilancia
                                  )}
                                </div>
                              </td>
                              <td className="p-sm">
                                {intervento.nome_cliente}
                              </td>
                              <td className="p-sm">
                                {dayjs(intervento.data_richiesta).format(
                                  'DD.MM.YYYY'
                                )}
                              </td>
                              <td className="p-sm">
                                {calcGiorni(intervento.data_richiesta)}
                              </td>
                            </tr>
                          </Fragment>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </StickyTable>
                {!isMobile && operatoreField}
              </CardBody>
            </Card>
          </div>
          <div className="col-md-4 pianificazione-dettagli">
            {isMobile && <div className="mb-4">{operatoreField}</div>}
            <CalendarField
              setDataIntervento={setDataIntervento}
              dataIntervento={dataIntervento}
              type="verifica_periodica"
            />
            <div className="form-group mt-4">
              <label className="label-m text-body-secondary">Note</label>
              <textarea
                className="form-control"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <PianificazioneFooter
        step={step}
        setStep={setStep}
        selectedCount={interventiSelected.length}
        canSave={interventiSelected.length > 0 && !!dataIntervento}
        onClose={toggle}
        onSave={() =>
          save({
            interventi: interventiSelected,
            data_intervento: dataIntervento,
            note,
            utente,
          })
        }
      />
    </Modal>
  )
}
